import React from 'react';
import { Mail, ExternalLink } from 'lucide-react';
import { useData } from '../context/DataContext';

const Footer = ({ setActiveTab }) => {
  const { projectInfo, partners, contact, loading } = useData();
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { id: 'home', name: 'Home' },
    { id: 'about', name: 'About' },
    { id: 'partners', name: 'Partners' },
    { id: 'activities', name: 'Activities' },
    { id: 'resources', name: 'Resources' },
    { id: 'impact', name: 'Impact' },
  ];

  if (loading || !projectInfo) {
    return null;
  }

  return (
    <footer className="bg-[#1e3a5f] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Project Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={projectInfo.logo}
                alt="Project Logo"
                className="h-16 w-16 object-contain"
              />
              <div>
                <h3 className="font-bold text-lg">{projectInfo.name}</h3>
                <p className="text-[#90cdf4] text-sm italic">"{projectInfo.tagline}"</p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              An Erasmus+ KA2 partnership project promoting conflict management skills 
              in schools across Europe through collaborative learning and exchange.
            </p>
            
            {/* EU Funding Disclaimer */}
            <div className="bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-xs text-white/80 leading-relaxed">
                <strong>Disclaimer:</strong> This project is co-funded by the European Union. 
                Views and opinions expressed are however those of the author(s) only and do not 
                necessarily reflect those of the European Union or the European Education and 
                Culture Executive Agency (EACEA). Neither the European Union nor EACEA can be 
                held responsible for them.
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => setActiveTab(link.id)}
                    className="text-white/70 hover:text-white hover:pl-2 transition-all duration-300 block"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Partners */}
          <div>
            <h4 className="font-bold text-lg mb-6">Partners</h4>
            <ul className="space-y-4">
              {(partners || []).map((partner) => (
                <li key={partner.id} className="flex items-start gap-3">
                  <span className="text-xl">{partner.flag}</span>
                  <div>
                    <p className="text-white/90 text-sm font-medium">{partner.name}</p>
                    <p className="text-white/60 text-xs">{partner.city}</p>
                  </div>
                </li>
              ))}
            </ul>

            {contact && (
              <div className="mt-8">
                <h4 className="font-bold text-lg mb-4">Contact</h4>
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 text-[#90cdf4] hover:text-white transition-colors duration-300"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{contact.email}</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm text-center sm:text-left">
              © {currentYear} {projectInfo.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-sm">Project Duration: {projectInfo.duration}</span>
              <span className="hidden sm:inline text-white/30">|</span>
              <a
                href="https://erasmus-plus.ec.europa.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[#90cdf4] hover:text-white text-sm transition-colors duration-300"
              >
                Erasmus+ Programme
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
